/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      if (window.LilLeoCart && typeof window.LilLeoCart.open === 'function') {
        window.LilLeoCart.fetchCart(() => window.LilLeoCart.open());
        return;
      }
      if (window.LilLeoCartBridge && typeof window.LilLeoCartBridge.open === 'function') {
        window.LilLeoCartBridge.open();
        return;
      }
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        if (window.LilLeoCart && typeof window.LilLeoCart.fetchCart === 'function') {
          window.LilLeoCart.fetchCart();
        }
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise poll and wait for async module scripts.
function bindActions() {
  if (window.Shopify?.actions?.openCart) {
    initStandardActions();
    return true;
  }
  return false;
}

if (!bindActions()) {
  document.addEventListener('DOMContentLoaded', bindActions, { once: true });
  const checkActionsInterval = setInterval(() => {
    if (bindActions()) {
      clearInterval(checkActionsInterval);
    }
  }, 50);
  setTimeout(() => clearInterval(checkActionsInterval), 6000);
}

// Silently suppress Conversion Bear widgets in this new theme without touching Live Theme
function suppressConversionBearWidgets() {
  const selectors = [
    'product-form iframe',
    '.product-form iframe',
    'form[action*="/cart/add"] iframe',
    'iframe[srcdoc*="trustBarContainer"]',
    'iframe[srcdoc*="badgesContainer"]',
    'iframe[srcdoc*="conversionbear"]',
    'iframe[srcdoc*="conversion-bear"]',
    '.badgesContainer',
    '.trustBarContainer',
    '#container-card',
    '[class*="conversion-bear"]',
    '[id*="conversion-bear"]',
    '.conversionbear-trust-badge',
    '#conversionbear-trust-badge'
  ];

  function hideAll() {
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('height', '0', 'important');
        try {
          el.remove();
        } catch(e) {}
      });
    });
  }

  hideAll();
  const observer = new MutationObserver(hideAll);
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
}
suppressConversionBearWidgets();

// =======================================================================
// CART POP ANIMATION TRIGGER (Header Pill Bounce on Add/Update)
// =======================================================================
function triggerCartPopAnimation() {
  const cartButtons = document.querySelectorAll('#header-cart-btn, #cart-icon-bubble, .lilleo-header-cart-btn');
  const countPills = document.querySelectorAll('#header-cart-count, .lilleo-cart-count');
  
  const targets = [...cartButtons, ...countPills];
  targets.forEach((el) => {
    el.classList.remove('animate-cart-pop');
    void el.offsetWidth; // Trigger reflow
    el.classList.add('animate-cart-pop');
    setTimeout(() => {
      el.classList.remove('animate-cart-pop');
    }, 500);
  });
}

window.triggerCartPop = triggerCartPopAnimation;
document.addEventListener('cart:updated', triggerCartPopAnimation);
document.addEventListener('cart:item-added', triggerCartPopAnimation);
document.addEventListener('cart-update', triggerCartPopAnimation);
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  try {
    subscribe(PUB_SUB_EVENTS.cartUpdate, triggerCartPopAnimation);
  } catch (e) {}
}
